/**
 * Hold to peek (§15.1): the content shows only while a finger (or the Space/Enter key) is held
 * on it. It hides on release, on pointercancel, when the pointer capture is lost, when the page
 * is hidden or loses focus, so a glance over the shoulder shows nothing. The veiled box keeps a
 * fixed minimum height, so its size reveals nothing about what is inside.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";

export interface HoldToPeekProps {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly children: ReactNode;
  readonly minHeight?: string;
  readonly className?: string;
  readonly testId?: string;
  /** Called with the release point just before the content hides (hold-and-slide selection). */
  readonly onRelease?: (point: { x: number; y: number } | null) => void;
  /** A key pressed while the content shows (e.g. arrow keys to move through cards). */
  readonly onShownKey?: (key: string) => void;
}

export function HoldToPeek({
  label,
  hint = "Hold to view · release to hide",
  children,
  minHeight = "9rem",
  className = "",
  testId,
  onRelease,
  onShownKey,
}: HoldToPeekProps) {
  const [shown, setShown] = useState(false);
  const hide = useCallback(() => setShown(false), []);

  useEffect(() => {
    if (!shown) return;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") hide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
    };
  }, [shown, hide]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={shown}
      data-testid={testId}
      data-shown={shown ? "true" : "false"}
      className={`peek ${shown ? "shown" : ""} ${className}`}
      style={{ minHeight }}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // jsdom and old browsers
        }
        setShown(true);
      }}
      onPointerUp={(e) => {
        if (shown) onRelease?.({ x: e.clientX, y: e.clientY });
        hide();
      }}
      onPointerCancel={hide}
      onLostPointerCapture={hide}
      onKeyDown={(e) => {
        if (shown && onShownKey && e.key !== " " && !e.repeat) {
          if (e.key.startsWith("Arrow") || e.key === "Enter") {
            e.preventDefault();
            onShownKey(e.key);
            return;
          }
        }
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          setShown(true);
        }
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") {
          if (shown) onRelease?.(null);
          hide();
        }
      }}
      onBlur={hide}
      onContextMenu={(e) => e.preventDefault()}
    >
      {shown ? (
        <div className="content">{children}</div>
      ) : (
        <div className="veil">
          <span>{label}</span>
          {hint ? <small>{hint}</small> : null}
        </div>
      )}
    </div>
  );
}
